import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveAccount, useActiveWallet, useDisconnect } from 'thirdweb/react';
import { User, LogOut, Shield, ChevronDown, Settings } from 'lucide-react';
import { isMasterAdminSync, MASTER_ADMIN_WALLET } from '../admin/constants';

interface UserDropdownProps {
  client: any;
  onSettingsClick?: () => void;
}

export default function UserDropdown({ client, onSettingsClick }: UserDropdownProps) {
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const { disconnect } = useDisconnect();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check admin status (master admin gets immediate access)
  useEffect(() => {
    if (account?.address) {
      // Master admin check is synchronous and immediate
      const isMaster = isMasterAdminSync(account.address);
      setIsAdmin(isMaster);
      
      // Could add database check for other admins here if needed
    } else {
      setIsAdmin(false);
    }
  }, [account?.address]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when account changes (disconnect/reconnect)
  useEffect(() => {
    setIsOpen(false);
  }, [account?.address]);

  const handleDisconnect = async () => {
    try {
      if (wallet) {
        await disconnect(wallet);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Error disconnecting:', error);
      setIsOpen(false);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setIsOpen(false);
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setIsOpen(false);
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
    }
    setIsOpen(false);
  };

  if (!account) return null;

  const truncatedAddress = `${account.address?.slice(0, 6)}...${account.address?.slice(-4)}`;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-[#2DD4BF]/10 hover:bg-[#2DD4BF]/20 border border-[#2DD4BF]/30 rounded-lg transition-all group"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2DD4BF] to-[#2DD4BF]/60 flex items-center justify-center">
          <User className="w-4 h-4 text-black" />
        </div>
        <div className="hidden sm:flex flex-col items-start">
          <span className="text-xs text-[#2DD4BF]/70 font-medium">Connected</span>
          <span className="text-sm text-white font-mono">{truncatedAddress}</span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-[#2DD4BF] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-[#1a1a1a] border border-[#2DD4BF]/30 rounded-lg shadow-xl shadow-[#2DD4BF]/10 overflow-hidden z-50">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-[#2DD4BF]/20 bg-[#2DD4BF]/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2DD4BF] to-[#2DD4BF]/60 flex items-center justify-center">
                <User className="w-5 h-5 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#2DD4BF]/70 font-medium uppercase tracking-wider">
                  Wallet Address
                </p>
                <p className="text-sm text-white font-mono truncate">
                  {account.address}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Profile Link */}
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-[#2DD4BF]/10 transition-colors group"
            >
              <User className="w-4 h-4 text-[#2DD4BF] group-hover:text-[#2DD4BF]" />
              <div className="flex-1 text-left">
                <div className="text-sm font-medium">My Profile</div>
                <div className="text-xs text-gray-400">View and edit your profile</div>
              </div>
            </button>

            {/* Profile Settings */}
            {onSettingsClick && (
              <button
                onClick={handleSettingsClick}
                className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-[#2DD4BF]/10 transition-colors group"
              >
                <Settings className="w-4 h-4 text-[#2DD4BF] group-hover:text-[#2DD4BF]" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium">Profile Settings</div>
                  <div className="text-xs text-gray-400">Update Marbleverse ID & city</div>
                </div>
              </button>
            )}

            {/* Admin Link (only for admin wallets) */}
            {isAdmin && (
              <>
                <div className="mx-4 my-2 border-t border-[#2DD4BF]/20" />
                <button
                  onClick={handleAdminClick}
                  className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-orange-500/10 transition-colors group"
                >
                  <Shield className="w-4 h-4 text-orange-500 group-hover:text-orange-400" />
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-orange-500 group-hover:text-orange-400">
                      Admin Portal
                    </div>
                    <div className="text-xs text-gray-400">Manage site content</div>
                  </div>
                </button>
              </>
            )}

            {/* Disconnect */}
            <div className="mx-4 my-2 border-t border-[#2DD4BF]/20" />
            <button
              onClick={handleDisconnect}
              className="w-full px-4 py-3 flex items-center gap-3 text-white hover:bg-red-500/10 transition-colors group"
            >
              <LogOut className="w-4 h-4 text-red-400 group-hover:text-red-300" />
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-red-400 group-hover:text-red-300">
                  Disconnect
                </div>
                <div className="text-xs text-gray-400">Sign out of your wallet</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

